import type { TranslationKey } from "@privacy/translations";
import type { FC, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type {
  ColumnHeaderFilterIconButtonProps,
  DataGridPremiumProps,
  GridApi,
  GridColumnMenuItemProps,
  GridColumnMenuProps,
  GridLocaleText,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { enUS, nbNO, svSE } from "@mui/x-data-grid-premium/locales";
import {
  DataGridPremium,
  GridColumnMenuFilterItem,
  GridColumnMenuHideItem,
  GridColumnMenuManageItem,
  gridClasses,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  gridExpandedSortedRowEntriesSelector,
  gridFilterModelSelector,
  gridPreferencePanelStateSelector,
  gridQuickFilterValuesSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import MenuList from "@mui/material/MenuList";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Checkbox } from "@privacy/ui";
import TextField from "@mui/material/TextField";
import { useTranslation } from "@privacy/translations";
import debounce from "@mui/utils/debounce";
import InputAdornment from "@mui/material/InputAdornment";
import { inputBaseClasses } from "@mui/material/InputBase";
import type { SupportedLanguage } from "../api/userInfo";
import { SearchIcon, ArrowDownIcon, ArrowUpIcon, Columns3Icon, EyeClosedIcon, FunnelIcon } from "./icons";
import { customScrollbar } from "./customScrollbar";

const {
  main,
  row,
  cell,
  columnHeader,
  virtualScroller,
  iconSeparator,
  menuIconButton,
  scrollbar,
  columnHeaders,
  scrollbarFiller,
  "root--noToolbar": rootNoToolbar,
} = gridClasses;

export type GridProps<Row extends GridValidRowModel = GridValidRowModel> = DataGridPremiumProps<Row> & {
  partsSx?: Partial<Record<"cellSx" | "mainSx" | "columnHeaderSx" | "rowSx" | "columnHeadersSx", SxProps<Theme>>>;
};

export const Grid = <Row extends GridValidRowModel>({ partsSx = {}, ...props }: GridProps<Row>) => {
  const theme = useTheme();
  const { palette } = theme;
  const { language } = useTranslation();

  const border = `1px solid ${palette.divider}`;

  const background = palette.background.paper;
  const selectedBackground = palette.action.selected;
  const hoverBackground = palette.action.hover;
  const paddingX = "12px";

  const scrollbarSize = "8px";

  return (
    <DataGridPremium
      {...props}
      slots={{
        columnSortedDescendingIcon: ColumnSortedDescendingIcon,
        columnSortedAscendingIcon: ColumnSortedAscendingIcon,
        columnMenu: CustomColumnMenu,
        columnMenuFilterIcon: ColumnMenuFilterIcon,
        columnMenuHideIcon: ColumnMenuHideIcon,
        columnMenuManageColumnsIcon: ColumnMenuManageColumnsIcon,
        columnHeaderFilterIconButton: CustomColumnHeaderFilterIcon,
        columnsPanel: CustomColumnsPanel,

        ...props.slots,
      }}
      slotProps={{ filterPanel: getFilterPanelProps(), ...props.slotProps }}
      columnHeaderHeight={props.columnHeaderHeight ?? 50}
      localeText={supportedLanguageToGridText[language]}
      sx={{
        "--DataGrid-pinnedBackground": background,
        "--DataGrid-containerBackground": background,
        "--DataGrid-rowBorderColor": palette.divider,
        "border": "none",
        [`& .${main}`]: { border: `1px solid ${palette.divider}`, background, ...partsSx.mainSx },
        [`& .${scrollbar}`]: { ...customScrollbar, scrollbarColor: "auto" },
        [`& .${scrollbarFiller}`]: { minWidth: `${scrollbarSize} !important` },
        [`&.${rootNoToolbar}`]: { borderRadius: "0 !important" },
        [`& .${columnHeaders}`]: partsSx.columnHeadersSx,
        [`& .${columnHeader}`]: {
          background,
          px: paddingX,
          pr: 0,
          borderRight: "none",
          ...insetOutline,
          ...partsSx.columnHeaderSx,
          [`&.${gridClasses["columnHeader--pinnedRight"]}`]: { background: "transparent" },
        },
        [`& .${virtualScroller}`]: { background },
        [`& .${row}`]: { background, borderBottom: border, ...partsSx.rowSx },
        [`& .${row}:hover:not(:has(button:hover, a:hover))`]: {
          background: hoverBackground,
          cursor: props.onRowSelectionModelChange || props.onRowClick ? "pointer" : "default",
        },
        [`&& .${row}.Mui-selected`]: { background: selectedBackground },
        [`& .${iconSeparator}`]: { color: "transparent" },
        [`& .${cell}`]: {
          ...insetOutline,
          borderTop: "none",
          borderRight: "none",
          px: paddingX,
          pr: 0,
          display: "flex",
          alignItems: "center",
          ...partsSx.cellSx,
        },
        [`& .${gridClasses["container--top"]}`]: { background },
        [`& .${gridClasses["cell--pinnedLeft"]}`]: { background: "inherit !important" },
        [`& .${gridClasses["cell--pinnedRight"]}`]: { background: "inherit !important", right: "0 !important" },
        [`& .${gridClasses["columnHeader--pinnedLeft"]},& .${gridClasses["columnHeader--pinnedRight"]}`]: {
          background,
        },
        [`& .${gridClasses["cell--pinnedRight"]},& .${gridClasses["columnHeader--pinnedRight"]}`]: {
          borderLeft: "none",
        },
        [`& .${gridClasses.filler} > *`]: { border: "none" },
        [`& .${menuIconButton}`]: {
          "color": theme => theme.palette.grey["900"],
          "&:hover": { backgroundColor: theme => theme.palette.action.hover },
        },
        ...props.sx,
      }}
    />
  );
};

const insetOutline: SxProps<Theme> = {
  "&:focus, &:focus-within": { outline: "none !important" },
  "&:focus-visible": {
    outline: "none !important",
    boxShadow: theme => `inset 0 0 0 2px ${theme.palette.primary["300"]}`,
    borderRadius: 1,
  },
};

const ColumnMenuFilterIcon: FC = () => <FunnelIcon size={16} color={theme => theme.palette.text.primary} />;
const ColumnMenuHideIcon: FC = () => <EyeClosedIcon size={16} color={theme => theme.palette.text.primary} />;
const ColumnMenuManageColumnsIcon: FC = () => <Columns3Icon size={16} color={theme => theme.palette.text.primary} />;

const ColumnSortedDescendingIcon: FC = () => <ArrowDownIcon size={12} color={theme => theme.palette.text.primary} />;

const ColumnSortedAscendingIcon: FC = () => <ArrowUpIcon size={12} color={theme => theme.palette.text.primary} />;

const getFilterPanelProps = (): NonNullable<DataGridPremiumProps["slotProps"]>["filterPanel"] => {
  const shared: SxProps = {
    ".MuiFormControl-root": { mr: 1 },
  };

  return {
    sx: {
      "&": { p: 2, pt: 2, pb: 1 },
      "& .MuiInputLabel-root": { display: "none !important" },

      [`.${gridClasses.filterForm}`]: { gap: 1, alignItems: "center" },
      [`.${gridClasses.filterFormDeleteIcon}`]: { order: 1 },
      [`.${gridClasses.filterFormDeleteIcon} .MuiButtonBase-root `]: { color: theme => theme.palette.text.primary },
      [`.${gridClasses.panelFooter} .MuiButton-startIcon`]: { display: "none" },
      [`.${gridClasses.panelFooter} .MuiButtonBase-root `]: {
        "backgroundColor": "transparent",
        "color": theme => theme.palette.primary.dark,
        "border": "none",
        "px": 2,
        "py": 1.2,
        "fontSize": theme => theme.typography.pxToRem(13),
        "fontWeight": 600,
        "&:hover": { bgcolor: theme => theme.alpha(theme.palette.primary.main, 0.04) },
      },
      [`.${gridClasses.filterFormColumnInput}`]: {
        ...shared,
        minWidth: "15rem",
        label: { display: "none !important" },
      },
    },
  };
};

const CustomColumnMenu = (props: GridColumnMenuProps) => {
  const itemProps: GridColumnMenuItemProps = {
    colDef: props.colDef,
    onClick: props.hideMenu,
  };

  return (
    <MenuList sx={{ py: 2, mt: 1.6, minWidth: "248px" }}>
      <GridColumnMenuFilterItem {...itemProps} />
      <GridColumnMenuHideItem {...itemProps} />
      <GridColumnMenuManageItem {...itemProps} />
    </MenuList>
  );
};

const CustomColumnHeaderFilterIcon = (props: ColumnHeaderFilterIconButtonProps) => {
  const { counter, field, onClick } = props;
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const preferencePanel = useGridSelector(apiRef, gridPreferencePanelStateSelector);
  const labelId = useId();
  const panelId = useId();

  const isFilterPanelOpen = preferencePanel.open && preferencePanel.labelId === labelId;

  const toggleFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (isFilterPanelOpen) {
        apiRef.current.hideFilterPanel();
      } else {
        apiRef.current.showFilterPanel(undefined, panelId, labelId);
      }

      if (onClick) {
        const columnHeaderParams = apiRef.current.getColumnHeaderParams(field);
        onClick(columnHeaderParams, event);
      }
    },
    [apiRef, field, onClick, panelId, labelId, isFilterPanelOpen]
  );

  const iconButton = (
    <rootProps.slots.baseIconButton
      id={labelId}
      onClick={toggleFilter}
      color="default"
      size="small"
      tabIndex={-1}
      aria-haspopup="menu"
      aria-expanded={isFilterPanelOpen}
      aria-controls={isFilterPanelOpen ? panelId : undefined}
      {...rootProps.slotProps?.baseIconButton}
    >
      <FunnelIcon size={14} color={theme => theme.palette.text.primary} />
    </rootProps.slots.baseIconButton>
  );

  const tooltipTitle = counter && apiRef.current.getLocaleText("columnHeaderFiltersTooltipActive")(counter);

  return (
    <rootProps.slots.baseTooltip title={tooltipTitle} enterDelay={1000} {...rootProps.slotProps?.baseTooltip}>
      <Box>
        {counter && counter > 1 && (
          <Badge badgeContent={counter} color="default">
            {iconButton}
          </Badge>
        )}
        {counter && counter <= 1 && iconButton}
      </Box>
    </rootProps.slots.baseTooltip>
  );
};

const supportedLanguageToGridText: Record<SupportedLanguage, Partial<GridLocaleText>> = {
  no: nbNO.components.MuiDataGrid.defaultProps.localeText,
  sv: svSE.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
};

/** https://github.com/mui/mui-x/blob/master/packages/grid/x-data-grid/src/components/panel/GridColumnsPanel.tsx */
const CustomColumnsPanel: FC = () => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const gridApiRef = useGridApiContext();
  const columns = useGridSelector(gridApiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(gridApiRef, gridColumnVisibilityModelSelector);
  const currentColumns = columns.filter(
    column =>
      column.field !== ACTIONS_COLUMN_FIELD && column.headerName?.toLowerCase().includes(searchValue.toLowerCase())
  );

  useEffect(() => searchInputRef.current?.focus(), []);

  const toggleAllColumns = (checked: boolean) => {
    const newModel = { ...columnVisibilityModel };
    columns.forEach(column => (newModel[column.field] = checked));
    gridApiRef.current.setColumnVisibilityModel(newModel);
  };

  return (
    <Stack spacing={2} sx={{ pt: 2, pb: 1 }}>
      <Box sx={{ px: 2 }}>
        <TextField
          fullWidth
          inputRef={searchInputRef}
          size="small"
          slotProps={{
            input: {
              sx: { borderRadius: 1 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={14} />
                </InputAdornment>
              ),
            },

            htmlInput: { "aria-label": t("DynamicFormGrid.ColumnPanel.SearchPlaceholder") },
          }}
          placeholder={t("DynamicFormGrid.ColumnPanel.SearchPlaceholder")}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />
      </Box>
      <Stack sx={{ width: "350px", maxHeight: "300px", overflowY: "auto" }}>
        {currentColumns.map(column => (
          <Stack
            component="label"
            spacing={1}
            key={column.field}
            direction="row"
            sx={{ px: 2, alignItems: "center", cursor: "pointer" }}
          >
            <Checkbox
              sx={{ p: 1 }}
              size="small"
              name={column.field}
              disabled={column.hideable === false}
              checked={columnVisibilityModel[column.field] !== false}
              onChange={e => gridApiRef.current.setColumnVisibility(column.field, e.target.checked)}
            />
            <Typography variant="body2">{column.headerName}</Typography>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" sx={{ px: 1, justifyContent: "space-between" }}>
        <Button
          sx={{ color: "primary.main", fontWeight: 600 }}
          size="small"
          variant="text"
          onClick={() => toggleAllColumns(false)}
        >
          {t("DynamicFormGrid.ColumnPanel.HideAll")}
        </Button>

        <Button
          sx={{ color: "primary.main", fontWeight: 600 }}
          size="small"
          variant="text"
          onClick={() => toggleAllColumns(true)}
        >
          {t("DynamicFormGrid.ColumnPanel.ShowAll")}
        </Button>
      </Stack>
    </Stack>
  );
};

type FooterProps = { children: ReactNode };
const Footer = ({ children }: FooterProps) => {
  return <Stack sx={{ py: 0.5 }}>{children}</Stack>;
};

/** Extract prefix from keys ending with Suffix. Then we do not have to pass two keys.
 * This can be removed when we have a translation library that can handle pluralisation */
type ExtractPrefix<Key extends TranslationKey, Suffix extends string> = {
  [Key in TranslationKey]: Key extends `${infer Prefix}${Suffix}` ? Prefix : never;
}[Key];

type NumberOfRowsProps = { textKey: ExtractPrefix<TranslationKey, ".Singular" | ".Plural"> };
const NumberOfRows = ({ textKey }: NumberOfRowsProps) => {
  const { t } = useTranslation();
  const numberOfRows = useGridSelector(useGridApiContext(), gridExpandedSortedRowEntriesSelector).length;

  return (
    <Typography sx={{ pl: 2, fontSize: theme => theme.typography.pxToRem(12) }}>
      <strong>{numberOfRows} </strong>
      {t(`${textKey}.${numberOfRows === 1 ? "Singular" : "Plural"}`)}
    </Typography>
  );
};

const SearchField: FC<{ gridApiRef: RefObject<GridApi>; placeholder?: string }> = ({ gridApiRef, placeholder }) => {
  const [searchValue, setSearchValue] = useState("");
  const quickFilterValue = useGridSelector(gridApiRef, gridQuickFilterValuesSelector);

  useLayoutEffect(() => setSearchValue((quickFilterValue ?? []).join(" ")), [quickFilterValue]);

  const updateSearchValue = useMemo(() => {
    return debounce((newValue: string) => {
      const filterModel = gridFilterModelSelector(gridApiRef);
      const quickFilterValues = newValue.split(" ").filter(word => word !== "");
      const isUnchanged =
        filterModel.quickFilterExcludeHiddenColumns === false &&
        (filterModel.quickFilterValues ?? []).join(" ") === quickFilterValues.join(" ");
      if (isUnchanged) return;
      gridApiRef.current.setFilterModel({ ...filterModel, quickFilterValues, quickFilterExcludeHiddenColumns: false });
    }, 200);
  }, [gridApiRef]);

  function handleSearchValueChange(newValue: string) {
    setSearchValue(newValue);
    updateSearchValue(newValue);
  }

  return (
    <TextField
      size="small"
      slotProps={{
        input: {
          sx: { borderRadius: 1 },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon size={14} />
            </InputAdornment>
          ),
        },

        htmlInput: { "aria-label": placeholder, "autoComplete": "off" },
      }}
      value={searchValue}
      sx={{
        [`.${inputBaseClasses.root}`]: { borderRadius: 1 },
        pt: /** This is added prevent the highlighting to be cut of when focused */ "1px",
      }}
      placeholder={placeholder}
      onChange={e => handleSearchValueChange(e.target.value)}
    />
  );
};

const ACTIONS_COLUMN_FIELD = "actions";
Grid.ACTIONS_COLUMN_FIELD = ACTIONS_COLUMN_FIELD;
Grid.SearchField = SearchField;
Grid.NumberOfRows = NumberOfRows;
Grid.Footer = Footer;
Grid.VALUE_GETTER_DELIMITER = ";";
